import React, {
    useContext,
    useState,
    useEffect,
    useMemo,
    useCallback,
} from 'react'
import dayjs from 'dayjs'
import {
    ComposedChart,
    Line,
    YAxis,
    XAxis,
    Tooltip,
    Label,
    CartesianGrid,
    ResponsiveContainer,
    ReferenceArea,
    Legend,
} from 'recharts'

import { DataContext } from '../DataContext'
import Message from '../../../../elem/chart/Message'
import ChartTooltip from '../../../../elem/chart/TooltipDetail'
import { dateToString } from '../../../../../utils/dateUtils'
import palette from '../../../../../utils/chart/palette'
import { getAxisYDomain } from '../../../../../utils/chart/slice'
import { roundToFourDigits } from '../../../../../utils/chart/values'

const CustomLegend = ({ legendPayload }) => {
    return (
        legendPayload && (
            <ul
                className="recharts-default-legend"
                style={{ textAlign: 'center' }}
            >
                {legendPayload.map((item, index) => (
                    <li
                        key={`line-legend-${index}`}
                        className={`recharts-legend-item legend-item-${index}`}
                        style={{ display: 'inline-block', marginRight: '10px' }}
                    >
                        <svg
                            className="recharts-surface"
                            width="14"
                            height="14"
                            viewBox="0 0 32 32"
                            version="1.1"
                            style={{
                                display: 'inline-block',
                                verticalAlign: 'middle',
                                marginRight: '4px',
                            }}
                        >
                            <path
                                stroke="none"
                                fill={item.color}
                                d="M0,4h32v24h-32z"
                                className="recharts-legend-icon"
                            ></path>
                        </svg>
                        <span className="recharts-legend-item-text">
                            {item.value}
                        </span>
                    </li>
                ))}
            </ul>
        )
    )
}

const DetailSampleChart = ({ headerHeight }) => {
    const {
        paramChartData,
        selectedParams,
        isLoading,
        selectedUnits,
        zoomTrigger,
        visibleChartTab,
        isExpanded,
        left,
        setLeft,
        right,
        setRight,
        top,
        setTop,
        bottom,
        setBottom,
    } = useContext(DataContext)
    const [data, setData] = useState([])

    const [refAreaLeft, setRefAreaLeft] = useState('')
    const [refAreaRight, setRefAreaRight] = useState('')

    useEffect(() => zoomOut(), [zoomTrigger])

    useEffect(() => {
        if (paramChartData) {
            const d = paramChartData
                .filter(
                    (d) =>
                        !!selectedParams.find(
                            (x) =>
                                x.ParamID === d.ParamID && x.Units === d.Units
                        )
                )
                .map((d) => ({
                    ...d,
                    SampleDate: dayjs(d.SampleDate).toDate().getTime(),
                })) // convert sample date to unix time for x-axis range
            setData(d)
        }
    }, [paramChartData, selectedParams])

    const formattedUnits = useMemo(
        () => (selectedUnits ? selectedUnits : 'units unknown'),
        [selectedUnits]
    )

    const groupKey = 'ParamAlias'

    // get the set of all unique values in the column specified by groupKey
    const uniqueGroups = useMemo(
        () =>
            data &&
            data.length &&
            [...new Set(data.map((x) => x[groupKey]))].sort(),
        [data]
    )

    const chartData = useMemo(() => {
        if (!data || !data.length) return []

        const dates = [...new Set(data.map((d) => d.SampleDate))]

        return dates.map((date) => {
            const dateData = { SampleDate: date }

            const dateSpecificData = data
                .filter(
                    (x) => x.SampleDate === date && x.Units === selectedUnits
                )
                .sort((a, b) => {
                    // nulls sort after anything else
                    if (a.SampleDepth === null && b.SampleDepth === null) {
                        return a.ResultID - b.ResultID
                    }
                    if (a.SampleDepth === null) {
                        return 1
                    }
                    if (b.SampleDepth === null) {
                        return -1
                    }
                    return a.SampleDepth - b.SampleDepth
                })

            //group all values by parameter
            const groupedByParam = {}
            dateSpecificData.forEach((d) => {
                const key = d[groupKey]
                if (!groupedByParam[key]) {
                    groupedByParam[key] = []
                }
                groupedByParam[key].push(d.ResultValue)
            })

            // create both individual and array properties
            Object.keys(groupedByParam).forEach((param) => {
                const values = groupedByParam[param]

                // Using the maximum value as the individual value
                dateData[param] = values[0]

                // Array of all values
                dateData[`${param}-All`] = values
            })

            return dateData
        })
    }, [data, selectedUnits, groupKey])

    useEffect(() => {
        setTop('dataMax')
        setBottom('dataMin')
    }, [chartData])

    const zoom = useCallback(() => {
        let RAL = refAreaLeft
        let RAR = refAreaRight
        if (RAL === RAR || RAR === '') {
            RAL = ''
            RAL = ''
            return
        }

        // xAxis domain
        if (RAL > RAR) {
            const temp = RAL
            RAL = RAR
            RAR = temp
        }

        // yAxis domain
        const leftAxisDomain = getAxisYDomain(
            RAL,
            RAR,
            (x) => !x.includes('SampleDate'),
            0,
            chartData,
            'SampleDate'
        )
        setBottom(leftAxisDomain[0])
        setTop(leftAxisDomain[1])
        setLeft(RAL)
        setRight(RAR)
        setRefAreaRight('')
        setRefAreaLeft('')
    }, [refAreaLeft, refAreaRight, chartData])

    const zoomOut = useCallback(() => {
        setRefAreaLeft('')
        setRefAreaRight('')
        setLeft('dataMin')
        setRight('dataMax')
        setTop('dataMax')
        setBottom('dataMin')
    }, [chartData])

    // generate a line component for each
    // individual line
    const Lines = useMemo(
        () =>
            uniqueGroups &&
            uniqueGroups.map((groupName, idx) => (
                <Line
                    type="monotone"
                    key={`bar-${idx}`}
                    dataKey={`${groupName}`}
                    stroke={palette[idx % palette.length]}
                    strokeWidth={0}
                    stackId={'shared'}
                    unit={formattedUnits}
                    dot={{
                        clipDot: false,
                        r: 3,
                        stroke: palette[idx % palette.length],
                        fill: palette[idx % palette.length],
                    }}
                    activeDot={{
                        r: 5,
                        stroke: palette[idx % palette.length],
                        fill: palette[idx % palette.length],
                    }}
                    isAnimationActive={false}
                />
            )),
        [uniqueGroups, chartData]
    )

    const legendData = useMemo(
        () =>
            uniqueGroups &&
            uniqueGroups.map((groupName, idx) => ({
                color: palette[idx % palette.length],
                type: 'line',
                dataKey: groupName,
                value: groupName,
            })),
        [uniqueGroups]
    )

    // if loading || no data, return nothing
    if (isLoading || visibleChartTab !== 'sampleResult') {
        return null
    }
    if (!data.length) {
        return <Message text={`No Chart Data Available`} />
    }

    // construct chart
    const yAxisLabel = `Result Value (${formattedUnits})`
    const xAxisLabel = 'Sample Date'

    return (
        <div className="chartWrapper">
            <div
                style={{
                    height: isExpanded
                        ? `calc(100% - ${headerHeight}px)`
                        : '100%',
                }}
            >
                <ResponsiveContainer width="100%" height={`100%`}>
                    <ComposedChart
                        data={chartData}
                        margin={{
                            top: 10,
                            right: 50,
                            left: 12,
                            bottom: 15,
                        }}
                        onMouseDown={(e) =>
                            e && e.activeLabel && setRefAreaLeft(e.activeLabel)
                        }
                        onMouseMove={(e) =>
                            e &&
                            e.activeLabel &&
                            refAreaLeft &&
                            setRefAreaRight(e.activeLabel)
                        }
                        onMouseUp={() => zoom()}
                    >
                        <CartesianGrid strokeDasharray="3 3" />
                        <XAxis
                            allowDataOverflow
                            type="number"
                            dataKey="SampleDate"
                            domain={[left, right]}
                            tickFormatter={(unixTime) => dateToString(unixTime)}
                        >
                            <Label
                                value={xAxisLabel}
                                offset={-10}
                                position="insideBottom"
                                className="nitrateChartXAxisLabel"
                            />
                        </XAxis>
                        <YAxis
                            allowDataOverflow
                            type="number"
                            tickFormatter={(v) => roundToFourDigits(v)}
                            domain={[bottom, top]}
                        >
                            <Label
                                value={yAxisLabel}
                                angle={-90}
                                // offset={20}
                                dx={-20}
                                position={{
                                    x: 0,
                                }}
                                // position="insideLeft"
                                className="detailChartYAxisLabel"
                            />
                        </YAxis>
                        <Tooltip
                            content={
                                <ChartTooltip
                                    roundValues={false}
                                    stagger={10}
                                />
                            }
                        />
                        <Legend
                            content={
                                <CustomLegend legendPayload={legendData} />
                            }
                            verticalAlign="top"
                            wrapperStyle={{
                                top: 1,
                                left: 0,
                                alignItems: 'center',
                                width: '100%',
                            }}
                        />
                        {Lines}
                        {refAreaLeft && refAreaRight ? (
                            <ReferenceArea
                                x1={refAreaLeft}
                                x2={refAreaRight}
                                strokeOpacity={0.3}
                            />
                        ) : null}
                    </ComposedChart>
                </ResponsiveContainer>
            </div>
        </div>
    )
}

export default DetailSampleChart
export { CustomLegend }
